/*
 * CovBufferModal — der gepufferte Verlauf eines Field-Clients: ansehen, und
 * auf ausdrücklichen Wunsch nachträglich in die Historie übernehmen.
 *
 * Der Aufbau folgt einer einzigen Regel: die beiden Aktionen dürfen nicht
 * verwechselbar sein.
 *
 *   Anzeigen     holt Rohdaten in den Browser. Es entsteht kein Punkt in der
 *                Influx. Ein Klick zu viel kostet nichts.
 *   Übernehmen   schreibt rückdatiert in dieselbe Historie, aus der Berichte
 *                und Abrechnung lesen. Nicht zurücknehmbar.
 *
 * Deshalb gibt es hier KEINEN gemeinsamen Absende-Knopf mit einer
 * Modus-Auswahl. Ein Auswahlfeld, dessen falscher Wert nicht rückholbar ist,
 * ist genau die Bauform, bei der jemand irgendwann das Falsche anklickt. Die
 * zwei Aktionen sind zwei getrennte Bereiche mit zwei getrennten Knöpfen, und
 * der schreibende verlangt zusätzlich ein getipptes Wort — nicht als Schikane,
 * sondern weil ein Tippfeld die einzige Bestätigung ist, die man nicht
 * versehentlich mit der Eingabetaste durchwinkt.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api, ApiError } from '../api/client';
import { showToast } from './Toaster';
import type { LeafDto } from '../api/types';
import {
  MultiSeriesChart,
  type ChartPoint,
  type SeriesDescriptor,
} from './charts/MultiSeriesChart';
import './hv-modal.css';
import './CovBufferModal.css';

/* -------------------------------------------------------------------------
 * Wire-Typen. Bewusst hier und nicht in api/types.ts: der Vertrag gehört zu
 * dieser einen Ansicht, und api/types.ts ist eine von mehreren Phasen
 * gleichzeitig angefasste Datei.
 * ---------------------------------------------------------------------- */

type BackfillMode = 'preview' | 'apply';

interface BackfillJobDto {
  id: string;
  plantId: string;
  mode: BackfillMode;
  status: 'Pending' | 'Running' | 'Completed' | 'Cancelled' | 'Failed';
  fromUtc: string;
  toUtc: string;
  leafCount: number;
  leavesDone: number;
  rowLimit: number;
  rowsFetched: number;
  rowsWritten: number;
  rowsSkipped: number;
  limitReached: boolean;
  oldestAvailableUtc: string | null;
  oldestSampleMovedDuringJob: boolean;
  requestedWindowFullyCovered: boolean;
  achievedRetentionSeconds: number | null;
  clientBufferDegraded: boolean;
  clientBufferDegradedReason: string | null;
  previewTruncated: boolean;
  attempts: number;
  startedAt: string | null;
  lastPageAt: string | null;
  finishedAt: string | null;
  error: string | null;
}

interface BackfillPreviewPoint {
  tsMs: number;
  v: number | null;
  q: string | null;
}

interface BackfillPreviewSeries {
  leafId: string;
  points: BackfillPreviewPoint[];
}

interface BackfillPreviewResponse {
  job: BackfillJobDto;
  series: BackfillPreviewSeries[];
  chronological: unknown[] | null;
}

interface Props {
  plantId: string;
  /** Die Datenpunkte, die der Operator im Chart ausgewählt hat. */
  leaves: LeafDto[];
  /** Vorbelegung des Zeitfensters, ms seit Epoch. */
  initialRangeMs?: [number, number];
  /** Optional: genau einen Field-Client ansprechen. */
  clientId?: string | null;
  onClose(): void;
}

const CONFIRM_WORD = 'UEBERNEHMEN';
const POLL_INTERVAL_MS = 1500;

function toLocalInput(ms: number): string {
  // <input type="datetime-local"> will Ortszeit ohne Zone.
  const d = new Date(ms - new Date(ms).getTimezoneOffset() * 60_000);
  return d.toISOString().slice(0, 16);
}

function fromLocalInput(value: string): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function formatUtc(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : iso;
}

function formatDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '—';
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  if (days > 0) return `${days} Tage ${hours} h`;
  const minutes = Math.floor((seconds % 3_600) / 60);
  return `${hours} h ${minutes} min`;
}

export function CovBufferModal({
  plantId, leaves, initialRangeMs, clientId, onClose,
}: Props) {
  const now = Date.now();
  const defaultFrom = initialRangeMs?.[0] ?? now - 24 * 3_600_000;
  const defaultTo = initialRangeMs?.[1] ?? now;

  const [fromInput, setFromInput] = useState(toLocalInput(defaultFrom));
  const [toInput, setToInput] = useState(toLocalInput(defaultTo));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<BackfillJobDto | null>(null);
  const [preview, setPreview] = useState<BackfillPreviewSeries[] | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const pollTimer = useRef<number | null>(null);

  const leafById = useMemo(() => {
    const m = new Map<string, LeafDto>();
    for (const leaf of leaves) m.set(leaf.id, leaf);
    return m;
  }, [leaves]);

  const stopPolling = useCallback(() => {
    if (pollTimer.current !== null) {
      window.clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [busy, onClose]);

  const loadPreview = useCallback(async (jobId: string) => {
    try {
      const response = await api.get<BackfillPreviewResponse>(
        `/api/plants/${plantId}/telemetry/backfill/${jobId}/preview`);
      setPreview(response.series);
      setJob(response.job);
    } catch (e) {
      // Die Vorschau ist Anzeige. Ein Fehler hier darf den Job-Status nicht
      // überschreiben — der ist die wichtigere Information.
      setError(e instanceof ApiError ? `Vorschau nicht abrufbar (${e.status}).` : String(e));
    }
  }, [plantId]);

  const poll = useCallback((jobId: string, mode: BackfillMode) => {
    stopPolling();
    pollTimer.current = window.setTimeout(async () => {
      try {
        const next = await api.get<BackfillJobDto>(
          `/api/plants/${plantId}/telemetry/backfill/${jobId}`);
        setJob(next);
        if (mode === 'preview') await loadPreview(jobId);
        if (next.status === 'Pending' || next.status === 'Running') {
          poll(jobId, mode);
        }
      } catch (e) {
        setError(e instanceof ApiError
          ? `Job-Status nicht abrufbar (${e.status}).`
          : String(e));
      }
    }, POLL_INTERVAL_MS);
  }, [plantId, loadPreview, stopPolling]);

  const start = useCallback(async (mode: BackfillMode) => {
    const from = fromLocalInput(fromInput);
    const to = fromLocalInput(toInput);
    if (from === null || to === null) {
      setError('Zeitfenster unvollständig.');
      return;
    }
    if (to <= from) {
      setError('Das Ende des Fensters muss nach dem Anfang liegen.');
      return;
    }
    if (leaves.length === 0) {
      setError('Kein Datenpunkt ausgewählt.');
      return;
    }

    setBusy(true);
    setError(null);
    setPreview(null);
    setJob(null);
    try {
      const created = await api.post<BackfillJobDto>(
        `/api/plants/${plantId}/telemetry/backfill`,
        {
          mode,
          fromUtc: new Date(from).toISOString(),
          toUtc: new Date(to).toISOString(),
          leafIds: leaves.map((l) => l.id),
          clientId: clientId ?? undefined,
        });
      setJob(created);
      poll(created.id, mode);
      showToast(mode === 'apply'
        ? {
            tone: 'warn',
            title: 'Übernahme läuft',
            message: 'Der Fortschritt steht in diesem Fenster. Rückgängig geht es nicht.',
          }
        : { tone: 'info', title: 'Pufferabfrage läuft' });
      if (mode === 'apply') {
        setConfirmText('');
        setConfirmChecked(false);
      }
    } catch (e) {
      if (e instanceof ApiError) {
        const body = e.body as { error?: string } | null;
        setError(body?.error ?? `Fehler ${e.status}.`);
      } else {
        setError(String(e));
      }
    } finally {
      setBusy(false);
    }
  }, [plantId, leaves, clientId, fromInput, toInput, poll]);

  const cancel = useCallback(async () => {
    if (!job) return;
    setBusy(true);
    try {
      const next = await api.post<BackfillJobDto>(
        `/api/plants/${plantId}/telemetry/backfill/${job.id}/cancel`);
      setJob(next);
      stopPolling();
    } catch (e) {
      setError(e instanceof ApiError ? `Abbruch fehlgeschlagen (${e.status}).` : String(e));
    } finally {
      setBusy(false);
    }
  }, [plantId, job, stopPolling]);

  const chartSeries = useMemo<SeriesDescriptor[]>(() => {
    if (!preview) return [];
    const result: SeriesDescriptor[] = [];
    for (const series of preview) {
      const leaf = leafById.get(series.leafId);
      if (!leaf) continue;
      const points: ChartPoint[] = series.points.map((p) => [p.tsMs, p.v]);
      result.push({ leaf, points, options: { renderType: 'line', visible: true } });
    }
    return result;
  }, [preview, leafById]);

  const running = job !== null && (job.status === 'Pending' || job.status === 'Running');
  const applyArmed = confirmChecked && confirmText.trim().toUpperCase() === CONFIRM_WORD;
  const previewPointCount = preview?.reduce((sum, s) => sum + s.points.length, 0) ?? 0;

  return createPortal(
    <div class="hv-modal-backdrop" onClick={() => !busy && onClose()}>
      <div class="hv-modal hv-modal--wide hv-cov-buffer" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Gepufferter Verlauf · {leaves.length} Datenpunkt(e)</h2>
          <button type="button" class="hv-modal__close" onClick={onClose}
                  aria-label="Schließen">×</button>
        </div>

        <div class="hv-modal__body hv-cov-buffer__body">
          <p class="hv-cov-buffer__lead">
            Der Field-Client hält alle Wertänderungen lokal vor — auch die, die nie in die
            Cloud gesendet wurden. Von hier lässt sich dieser Puffer ansehen und, wenn
            gewünscht, nachträglich in die Historie übernehmen.
          </p>

          <div class="hv-cov-buffer__range">
            <label class="hv-cov-buffer__field">
              <span>Von</span>
              <input type="datetime-local" value={fromInput} disabled={busy || running}
                     onInput={(e) => setFromInput((e.target as HTMLInputElement).value)} />
            </label>
            <label class="hv-cov-buffer__field">
              <span>Bis</span>
              <input type="datetime-local" value={toInput} disabled={busy || running}
                     onInput={(e) => setToInput((e.target as HTMLInputElement).value)} />
            </label>
          </div>

          <div class="hv-cov-buffer__actions">
            {/* --- Sicher: nur ansehen ---------------------------------- */}
            <section class="hv-cov-buffer__lane hv-cov-buffer__lane--read">
              <h3 class="hv-cov-buffer__lane-title">Anzeigen</h3>
              <p class="hv-cov-buffer__lane-note">
                Holt die Rohwerte in dieses Fenster. <strong>Es wird nichts
                gespeichert</strong> — kein Punkt in der Historie, keine Zeile in der
                Datenbank. Beim Schließen ist die Anzeige weg.
              </p>
              <button type="button" class="hv-btn hv-btn--primary"
                      disabled={busy || running || leaves.length === 0}
                      onClick={() => start('preview')}>
                Puffer anzeigen
              </button>
            </section>

            {/* --- Nicht zurücknehmbar: schreiben ----------------------- */}
            <section class="hv-cov-buffer__lane hv-cov-buffer__lane--write">
              <h3 class="hv-cov-buffer__lane-title">Übernehmen</h3>
              <p class="hv-cov-buffer__lane-note">
                Schreibt die gepufferten Werte mit ihrem <strong>ursprünglichen
                Zeitstempel</strong> in die Historie. Dieselbe Historie, aus der Charts,
                Berichte und Abrechnung lesen. <strong>Das ist nicht
                zurücknehmbar.</strong>
              </p>
              <label class="hv-cov-buffer__confirm-row">
                <input type="checkbox" checked={confirmChecked} disabled={busy || running}
                       onChange={(e) => setConfirmChecked((e.target as HTMLInputElement).checked)} />
                <span>
                  Mir ist klar, dass {leaves.length} Datenpunkt(e) im gewählten Fenster
                  dauerhaft in die Historie geschrieben werden.
                </span>
              </label>
              <label class="hv-cov-buffer__field">
                <span>Zur Bestätigung „{CONFIRM_WORD}" eingeben</span>
                <input type="text" value={confirmText} disabled={busy || running}
                       autocomplete="off" spellcheck={false}
                       onInput={(e) => setConfirmText((e.target as HTMLInputElement).value)} />
              </label>
              <button type="button" class="hv-btn hv-btn--danger"
                      disabled={busy || running || !applyArmed || leaves.length === 0}
                      onClick={() => start('apply')}>
                In die Historie übernehmen
              </button>
            </section>
          </div>

          {error && <p class="hv-modal__error">{error}</p>}

          {job && (
            <section class="hv-cov-buffer__report">
              <h3 class="hv-cov-buffer__lane-title">
                {job.mode === 'apply' ? 'Übernahme' : 'Abfrage'} · {job.status}
              </h3>

              <dl class="hv-cov-buffer__stats">
                <div><dt>Geliefert</dt><dd>{job.rowsFetched.toLocaleString()} Zeilen</dd></div>
                {job.mode === 'apply' && (
                  <div><dt>Geschrieben</dt><dd>{job.rowsWritten.toLocaleString()} Zeilen</dd></div>
                )}
                <div><dt>Datenpunkte fertig</dt><dd>{job.leavesDone} / {job.leafCount}</dd></div>
                <div><dt>Grenze</dt><dd>{job.rowLimit.toLocaleString()} Zeilen</dd></div>
                <div>
                  <dt>Ältestes im Puffer</dt>
                  <dd>{formatUtc(job.oldestAvailableUtc)}</dd>
                </div>
                <div>
                  <dt>Erreichte Aufbewahrung</dt>
                  <dd>{formatDuration(job.achievedRetentionSeconds)}</dd>
                </div>
              </dl>

              {/* Die ehrlichen Meldungen. Sie stehen absichtlich hier und
                  nicht in einem Toast: ein Toast verschwindet, und wer den
                  Bericht später ansieht, muss sie noch sehen. */}
              {job.oldestSampleMovedDuringJob && (
                <p class="hv-cov-buffer__warn">
                  Der Puffer hat während des Laufs am älteren Ende aufgeräumt: das
                  älteste verfügbare Sample liegt jetzt bei{' '}
                  {formatUtc(job.oldestAvailableUtc)}. Was davor lag, war zum Zeitpunkt
                  des Zugriffs nicht mehr da — dieser Lauf hat es nicht geliefert.
                </p>
              )}
              {!job.requestedWindowFullyCovered && !job.oldestSampleMovedDuringJob && (
                <p class="hv-cov-buffer__warn">
                  Der Puffer reicht nicht bis zum Anfang des gewählten Fensters. Ältester
                  vorhandener Wert: {formatUtc(job.oldestAvailableUtc)}.
                </p>
              )}
              {job.limitReached && (
                <p class="hv-cov-buffer__warn">
                  Die Zeilengrenze des Lizenzmodells ({job.rowLimit.toLocaleString()}) ist
                  erreicht. Das Fenster ist damit nicht vollständig abgearbeitet — ein
                  weiterer Lauf setzt dort an, wo dieser aufgehört hat.
                </p>
              )}
              {job.rowsSkipped > 0 && (
                <p class="hv-cov-buffer__note">
                  {job.rowsSkipped.toLocaleString()} gelieferte Zeilen waren nicht
                  numerisch (Text- oder Zustandswerte) und gehen wie im Live-Pfad nicht in
                  die Historie.
                </p>
              )}
              {job.previewTruncated && (
                <p class="hv-cov-buffer__warn">
                  Die Anzeige ist abgeschnitten — es liegen mehr Werte im Puffer, als
                  dieses Fenster darstellt. Zeitfenster verkleinern oder weniger
                  Datenpunkte wählen.
                </p>
              )}
              {job.clientBufferDegraded && (
                <p class="hv-cov-buffer__warn">
                  Der Field-Client meldet seinen Puffer als eingeschränkt
                  {job.clientBufferDegradedReason ? `: ${job.clientBufferDegradedReason}` : ''}.
                  Die lokale Historie hat dort wahrscheinlich Lücken.
                </p>
              )}
              {job.error && <p class="hv-modal__error">{job.error}</p>}

              {running && (
                <button type="button" class="hv-btn hv-btn--sm" onClick={cancel} disabled={busy}>
                  Abbrechen
                </button>
              )}
            </section>
          )}

          {job?.mode === 'preview' && chartSeries.length > 0 && (
            <section class="hv-cov-buffer__chart">
              <h3 class="hv-cov-buffer__lane-title">
                Rohwerte aus dem Puffer · {previewPointCount.toLocaleString()} Punkte
              </h3>
              <p class="hv-cov-buffer__lane-note">
                Ungefiltert und unaggregiert, so wie der Client sie aufgezeichnet hat.
                Nur in diesem Browser.
              </p>
              <div class="hv-cov-buffer__chart-canvas">
                <MultiSeriesChart series={chartSeries} />
              </div>
            </section>
          )}
        </div>

        <div class="hv-modal__footer">
          <div class="hv-modal__footer-spacer" />
          <button type="button" class="hv-btn" onClick={onClose} disabled={busy}>
            Schließen
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
